import { useState } from "react";
import { SubscriptionCoTermStyled } from "../SubscriptionStyled";
import { AnimatePresence, motion } from "framer-motion";
import { IoChevronDownOutline, IoChevronUp } from "react-icons/io5";

export default function CoTerm(): JSX.Element {
  const terms = [
    {
      title: "What is Co-Term?",
      content: (
        <>
          <p>
            Co-term, or co-termination, refers to adjusting the subscription
            term by combining existing and new subscriptions with one expiration
            date, regardless of the purchase date.
          </p>
          <p>
            This also means that if an account has multiple subscriptions taken
            out at different times, they are bundled together to share the same
            renewal or co-term renewal date, rather than all subscriptions being
            due for renewal at various times.
          </p>
          <p>
            Video:{" "}
            <a
              href="https://youtu.be/hFiHmRtMdng"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch on YouTube
            </a>
          </p>
        </>
      ),
    },
    {
      title: "What are the Benefits of Co-Term for End-Users?",
      content: (
        <>
          <p>
            Co-term simplifies the subscription renewal process and billing
            cycle and makes it more efficient.
          </p>
          <p>
            Co-terming reduces the risk of subscriptions expiring unknowingly,
            which can impact daily operations and prevent you from accessing
            your data.
          </p>
          <p>
            Co-terming converts all subscriptions into a single contract, saving
            administrative resources, time and effort.
          </p>
        </>
      ),
    },
    {
      title: "Co-Term Examples",
      content: (
        <>
          <p>
            The co-termination date is a weighted average of the licenses
            purchased and claimed by your organization. Example A (multiple
            subscriptions purchased on the same date): An organization had two
            separate myDW subscriptions, one for 2x single channel subscriptions
            spanning one year (12 months) and another for 1x single channel
            subscription spanning three years (36 months). The co-termination
            value would be calculated as ((36*1)+(12*2))/3= 20 months total for
            all three subscriptions. So, assuming all three licenses were
            applied on the same day, the organization would have a co-term date
            of 20 months from the start date of the subscriptions. Example B
            (multiple subscriptions purchased on different dates): If the
            subscriptions were not applied simultaneously, for example, if the
            three-year subscription was applied halfway(6 month) through the
            one-year subscription, the co-term calculation would take that into
            effect. In this case, the calculation would be ((36*1)+(6*2))/3=16
            months total for all three subscriptions. All 3 channels will expire
            in 16 months.
          </p>
        </>
      ),
    },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <SubscriptionCoTermStyled.TermWrapper id="co-term-info">
      {terms.map((term, index) => {
        const isOpen = openIndex === index;
        return (
          <SubscriptionCoTermStyled.TermItem key={index}>
            <div
              className="title"
              onClick={() => handleToggle(index)}
              data-testid="subscription-history-history-div-toggle"
            >
              <h2>{term.title}</h2>
              <span className="icon">
                {isOpen ? <IoChevronUp /> : <IoChevronDownOutline />}
              </span>
            </div>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  key="content"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "linear" }}
                  style={{ overflow: "hidden" }}
                  className="content"
                >
                  {term.content}
                </motion.div>
              )}
            </AnimatePresence>
          </SubscriptionCoTermStyled.TermItem>
        );
      })}
    </SubscriptionCoTermStyled.TermWrapper>
  );
}
